import React from "react";
import { Lock } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import EmptyState from "./EmptyState";

/**
 * RequirePermission — wrap any element/route in a server-aligned permission
 * gate. The user needs **any** of the listed `permission` keys to see the
 * children; otherwise we render `fallback` (default: a friendly 403 panel).
 *
 * Two ways to use it:
 *
 * 1. As a route guard inside a <Route element=...> prop:
 *      <RequirePermission permission="settings.write">
 *        <PermissionSettings />
 *      </RequirePermission>
 *
 * 2. To conditionally render UI inside a page:
 *      <RequirePermission permission="leaves.approve" fallback={null}>
 *        <ApproveButton ... />
 *      </RequirePermission>
 *
 * Note: this is a presentational gate. The backend's `@PreAuthorize` is the
 * actual security boundary. If a user bypasses this client check (devtools,
 * stale cache, etc.) they still get a 403 from the API.
 */
export default function RequirePermission({
  permission,
  permissions, // alias accepting an array
  fallback,
  children,
}) {
  const { hasPermission } = useAuth();
  const required = Array.isArray(permissions)
    ? permissions
    : permission
    ? [permission]
    : [];

  // No permission requested == always show. Treat the empty case as
  // "no gating" so callers can spread props conditionally.
  const allowed = required.length === 0 || required.some((k) => hasPermission(k));

  if (allowed) return <>{children}</>;

  if (fallback !== undefined) return <>{fallback}</>;

  // Default fallback: a 403-style panel that matches the rest of the admin UI.
  return (
    <div className="p-6">
      <EmptyState
        icon={Lock}
        title="You don't have access to this page"
        subtitle={
          required.length === 1
            ? `Required permission: ${required[0]}`
            : `Required permissions: ${required.join(", ")}`
        }
      />
    </div>
  );
}
